import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, AlertOctagon, ChevronRight, Bell, Shield, Receipt, Gavel, BadgeDollarSign, ShieldAlert, GraduationCap, Stethoscope, FileText, User, Phone, Mail, UserCheck, Wallet } from "lucide-react";

const SEV = {
  alta:  { color: "#DC2626", label: "Crítico" },
  media: { color: "#E08A1F", label: "Importante" },
  baja:  { color: "#3B82F6", label: "Aviso" },
};

const ICON_TIPO = {
  aval_vencimiento: Shield,
  factura_vencida: Receipt,
  licitacion_plazo: Gavel,
  garantia_estancada: BadgeDollarSign,
  cae_doc: FileText,
  cae_prl: GraduationCap,
  cae_med: Stethoscope,
  tension_caja: Wallet,
};

const POLL_MS = 60000;

export default function DashboardAlertsCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState("miDia"); // miDia | equipo

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  if (!data) return null;

  const myItems = data.items.filter((n) => n.responsable?.id && user?.id && n.responsable.id === user.id);
  const teamItems = data.items.filter((n) => !myItems.some((mi) => mi.id === n.id));
  const items = view === "miDia" ? myItems : teamItems;
  const total = items.length;
  const critical = items.filter((n) => n.severidad === "alta").length;
  const cae = items.filter((n) => n.modulo === "cae").length;
  const finanzas = items.filter((n) => ["facturacion", "avales", "garantias"].includes(n.modulo)).length;

  const tiles = [
    { label: view === "miDia" ? "Mis alertas" : "Alertas del equipo", value: total, color: "#475569", Icon: Bell },
    { label: "Críticas",         value: critical, color: "#DC2626", Icon: AlertOctagon, pulse: critical > 0 },
    { label: "Alertas CAE",      value: cae,      color: "#7E22CE", Icon: ShieldAlert, pulse: cae > 0 },
    { label: "Avales / Facturas / Garantías", value: finanzas, color: "#0F8A5F", Icon: BadgeDollarSign },
  ];

  const visible = expanded ? items : items.slice(0, 6);

  return (
    <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="mb-8 md:mb-10" data-testid="dashboard-alerts-center">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#DC2626]/15 text-[#DC2626] border border-[#DC2626]/30">
            <AlertTriangle className="h-3.5 w-3.5" />
          </div>
          <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-[var(--legatum-text-muted)]">Centro de alertas</h2>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-muted)] ml-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Auto-refresco
          </span>
        </div>
        <Link to="/cae" className="inline-flex items-center gap-1 text-xs font-bold text-[var(--legatum-accent)] hover:underline">
          Ver CAE consolidado <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Toggle Mi día / Equipo */}
      <div className="mb-4 inline-flex rounded-xl bg-[var(--legatum-elevated)] border border-[var(--legatum-border)] p-1 gap-1" data-testid="alerts-view-toggle">
        <button type="button" onClick={() => { setView("miDia"); setExpanded(false); }} data-testid="alerts-view-midia"
          className="px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-1.5"
          style={view === "miDia" ? { background: "var(--legatum-accent)", color: "var(--legatum-accent-fg)" } : { color: "var(--legatum-text-muted)" }}>
          <UserCheck className="h-3.5 w-3.5" /> Mi día
          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums"
            style={view === "miDia" ? { background: "rgba(0,0,0,0.18)", color: "var(--legatum-accent-fg)" } : { background: "var(--legatum-surface)", color: "var(--legatum-text-secondary)", border: "1px solid var(--legatum-border)" }}>
            {myItems.length}
          </span>
        </button>
        <button type="button" onClick={() => { setView("equipo"); setExpanded(false); }} data-testid="alerts-view-equipo"
          className="px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-1.5"
          style={view === "equipo" ? { background: "var(--legatum-accent)", color: "var(--legatum-accent-fg)" } : { color: "var(--legatum-text-muted)" }}>
          <AlertTriangle className="h-3.5 w-3.5" /> Equipo
          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums"
            style={view === "equipo" ? { background: "rgba(0,0,0,0.18)", color: "var(--legatum-accent-fg)" } : { background: "var(--legatum-surface)", color: "var(--legatum-text-secondary)", border: "1px solid var(--legatum-border)" }}>
            {teamItems.length}
          </span>
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4" data-testid="dashboard-alerts-tiles">
        {tiles.map((t, i) => {
          const Icon = t.Icon;
          return (
            <motion.div key={t.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="relative rounded-xl p-4 overflow-hidden transition-colors"
              style={{
                background: "var(--legatum-surface)",
                border: "1px solid var(--legatum-border)",
              }}>
              <span aria-hidden className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full"
                    style={{ background: t.color, opacity: 0.85 }} />
              <div className="relative flex items-start justify-between gap-2 pl-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--legatum-text-muted)] truncate">{t.label}</div>
                  <div className="mt-1.5 text-3xl font-extrabold tabular-nums leading-none text-[var(--legatum-text)]">{t.value}</div>
                </div>
                <div className="relative h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: `${t.color}14`, border: `1px solid ${t.color}33` }}>
                  {t.pulse && t.value > 0 && <span className="absolute inset-0 rounded-lg animate-ping opacity-40" style={{ background: `${t.color}33` }} />}
                  <Icon className="h-4 w-4 relative" style={{ color: t.color }} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {total === 0 ? (
        <div className="rounded-2xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] p-6 text-center text-sm text-[var(--legatum-text-muted)]" data-testid="dashboard-alerts-empty">
          {view === "miDia"
            ? "✓ No tienes alertas asignadas directamente — disfruta tu día."
            : "✓ El equipo está al día — no hay alertas pendientes."}
        </div>
      ) : (
        <div className="rounded-2xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] overflow-hidden">
          <AnimatePresence initial={false}>
            {visible.map((n, i) => {
              const sev = SEV[n.severidad] || SEV.baja;
              const Icon = ICON_TIPO[n.tipo] || AlertTriangle;
              return (
                <motion.div key={n.id} layout initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ delay: Math.min(i * 0.02, 0.4) }}>
                  <div role="link" tabIndex={0} onClick={() => navigate(n.ruta || "/")}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(n.ruta || "/"); } }}
                    data-testid={`dashboard-alert-${n.id}`}
                    style={{
                      background: `linear-gradient(90deg, ${sev.color}1A 0%, ${sev.color}0A 35%, transparent 100%)`,
                      borderLeft: `4px solid ${sev.color}`,
                      boxShadow: n.severidad === "alta" ? `inset 0 0 0 1px ${sev.color}33` : undefined,
                    }}
                    className="block px-4 py-3 transition-colors duration-200 hover:brightness-105 border-b border-[var(--legatum-border)] last:border-b-0 relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--legatum-accent)]/40">
                    <div className="flex items-center gap-3 pl-1">
                      <div className="h-9 w-9 rounded-lg shrink-0 flex items-center justify-center relative"
                           style={{ background: `${sev.color}26`, border: `1px solid ${sev.color}66`, color: sev.color }}>
                        {n.severidad === "alta" && (
                          <span className="absolute inset-0 rounded-lg animate-pulse opacity-40" style={{ background: `${sev.color}33` }} />
                        )}
                        <Icon className="h-4 w-4 relative" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold truncate text-[var(--legatum-text)]">{n.titulo}</span>
                          <span style={{ background: `${sev.color}1F`, color: sev.color, border: `1px solid ${sev.color}44` }} className="rounded-md uppercase text-[9px] tracking-wider font-bold px-1.5 py-0.5">{sev.label}</span>
                        </div>
                        <div className="mt-0.5 text-xs text-[var(--legatum-text-muted)] font-medium truncate">{n.detalle}</div>
                        {n.responsable && (
                          <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`alert-responsable-${n.id}`}>
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[var(--legatum-elevated)] border border-[var(--legatum-border)] text-[var(--legatum-text-secondary)]">
                              <User className="h-3 w-3" /> {n.responsable.name || "Responsable"}
                            </span>
                            {n.responsable.telefono && (
                              <a href={`tel:${n.responsable.telefono}`} onClick={(e) => e.stopPropagation()}
                                 className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-[var(--legatum-elevated)] text-[var(--legatum-text-secondary)] border border-[var(--legatum-border)] hover:bg-[var(--legatum-surface)] hover:text-[var(--legatum-text)] transition-colors">
                                <Phone className="h-3 w-3" /> {n.responsable.telefono}
                              </a>
                            )}
                            {n.responsable.email && (
                              <a href={`mailto:${n.responsable.email}`} onClick={(e) => e.stopPropagation()}
                                 className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-[var(--legatum-elevated)] text-[var(--legatum-text-secondary)] border border-[var(--legatum-border)] hover:bg-[var(--legatum-surface)] hover:text-[var(--legatum-text)] transition-colors">
                                <Mail className="h-3 w-3" /> Email
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-[var(--legatum-text-muted)] shrink-0" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
          {items.length > 6 && (
            <button onClick={() => setExpanded((v) => !v)} data-testid="dashboard-alerts-toggle"
              className="w-full py-2.5 text-xs font-bold text-[var(--legatum-accent)] hover:bg-[var(--legatum-elevated)] transition-colors border-t border-[var(--legatum-border)]">
              {expanded ? `Ver menos` : `Ver ${items.length - 6} alertas más`}
            </button>
          )}
        </div>
      )}
    </motion.section>
  );
}
